import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cloudflareApi } from '../api/cloudflare';

const KEY_CONFIG = ['cloudflare', 'config'] as const;
const KEY_ZONES = ['cloudflare', 'zones'] as const;

export function useCloudflareConfig() {
  return useQuery({
    queryKey: KEY_CONFIG,
    queryFn: () => cloudflareApi.getConfig(),
    staleTime: 30_000,
  });
}

export function useCloudflareZones(enabled = true) {
  return useQuery({
    queryKey: KEY_ZONES,
    queryFn: () => cloudflareApi.listZones(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSaveCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apiToken: string) => cloudflareApi.saveCredentials(apiToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_CONFIG });
      qc.invalidateQueries({ queryKey: KEY_ZONES });
    },
  });
}

export function useCloudflareSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { zoneId: string; subdomain: string; tunnelName?: string }) =>
      cloudflareApi.setup(req.zoneId, req.subdomain, req.tunnelName),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY_CONFIG }),
  });
}

export function useServiceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: 'start' | 'stop' | 'reinstall') => {
      if (action === 'start') return cloudflareApi.serviceStart();
      if (action === 'stop') return cloudflareApi.serviceStop();
      return cloudflareApi.serviceReinstall();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY_CONFIG }),
  });
}

export function useTeardown() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cloudflareApi.teardown(),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY_CONFIG }),
  });
}
